import { useEffect, useState } from "react";

interface UseEntireScreenShareDetectionOptions {
  screenStream: MediaStream | null;
  isScreenSharing: boolean;
  pollIntervalMs?: number;
}

/**
 * Detects whether the user is sharing their entire screen (monitor) instead of a tab/window.
 * Uses the MediaStreamTrack settings plus a fallback interval to keep the state fresh.
 */
export const useEntireScreenShareDetection = ({
  screenStream,
  isScreenSharing,
  pollIntervalMs = 1000,
}: UseEntireScreenShareDetectionOptions) => {
  const [isEntireScreenShared, setIsEntireScreenShared] = useState(false);
  const [showTabWindowWarning, setShowTabWindowWarning] = useState(false);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let videoTrack: MediaStreamTrack | undefined;

    const resetState = () => {
      setIsEntireScreenShared(false);
      setShowTabWindowWarning(false);
    };

    if (!screenStream || !isScreenSharing) {
      resetState();
      return undefined;
    }

    const videoTracks = screenStream.getVideoTracks();
    if (videoTracks.length === 0) {
      resetState();
      return undefined;
    }

    [videoTrack] = videoTracks;

    const checkDisplaySurface = () => {
      if (!videoTrack) {
        resetState();
        return;
      }

      try {
        const settings = videoTrack.getSettings();
        const displaySurface = (settings as any).displaySurface as string | undefined;

        if (displaySurface === "monitor") {
          setIsEntireScreenShared(true);
          setShowTabWindowWarning(false);
        } else {
          setIsEntireScreenShared(false);
          setShowTabWindowWarning(true);
        }
      } catch (error) {
        setIsEntireScreenShared(false);
        setShowTabWindowWarning(true);
      }
    };

    checkDisplaySurface();

    const handleSettingsChange = () => {
      checkDisplaySurface();
    };

    if ("onsettingschange" in videoTrack) {
      videoTrack.addEventListener("settingschange" as any, handleSettingsChange);
    }

    intervalId = setInterval(checkDisplaySurface, pollIntervalMs);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (videoTrack && "onsettingschange" in videoTrack) {
        videoTrack.removeEventListener("settingschange" as any, handleSettingsChange);
      }
    };
  }, [screenStream, isScreenSharing, pollIntervalMs]);

  return {
    isEntireScreenShared,
    showTabWindowWarning,
  };
};

